// apps/client/src/net/interp.ts — remote residents rendered ~100 ms behind server time (§4.7)
import { tuning } from '@whiskerton/content';

const MAX = 20;   // samples kept per resident (≈ 2 s at the 10 Hz relay); a memory cap, not a feel number
const lerpAngle = (a: number, b: number, t: number) => { const d = ((b - a + Math.PI * 3) % (Math.PI * 2)) - Math.PI; return a + d * t; };

export class InterpBuffer<T extends { x: number; y: number; f: number }> {
  private samples: { at: number; s: T }[] = [];
  push(serverAt: number, s: T): void {
    const last = this.samples.at(-1);
    if (last && serverAt <= last.at) return;
    if (last && Math.hypot(s.x - last.s.x, s.y - last.s.y) > tuning.net.snapTiles) this.samples = [];   // teleport / door: snap, don't slide
    this.samples.push({ at: serverAt, s });
    if (this.samples.length > MAX) this.samples.shift();
  }
  /** Forget every sample (a snap: space.enter or a resync), so stale ones never pull a resident back. */
  clear(): void { this.samples = []; }
  /** renderAt = clock.now() − tuning.net.interpDelayMs. Before the first sample: the first; past the last: extrapolate ≤ extrapolateMs. */
  sample(renderAt: number): T | null {
    const n = this.samples.length;
    if (n === 0) return null;
    const first = this.samples[0]!, last = this.samples[n - 1]!;
    if (n === 1 || renderAt <= first.at) return renderAt <= first.at ? first.s : last.s;
    if (renderAt >= last.at) {
      const prev = this.samples[n - 2]!, span = last.at - prev.at, ahead = Math.min(renderAt - last.at, tuning.net.extrapolateMs);
      const k = span > 0 ? ahead / span : 0;
      return { ...last.s, x: last.s.x + (last.s.x - prev.s.x) * k, y: last.s.y + (last.s.y - prev.s.y) * k };
    }
    let i = n - 2;
    while (i > 0 && this.samples[i]!.at > renderAt) i--;
    const a = this.samples[i]!, b = this.samples[i + 1]!, t = (renderAt - a.at) / (b.at - a.at);
    return { ...(t < 0.5 ? a.s : b.s), x: a.s.x + (b.s.x - a.s.x) * t, y: a.s.y + (b.s.y - a.s.y) * t, f: lerpAngle(a.s.f, b.s.f, t) };
  }
}
